// src/components/layout/WorkspaceLayout.tsx
import { Outlet, useLocation, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Sidebar } from "./Sidebar";
import { Toolbar } from "./Toolbar";
import { TabsSection } from "./tabs-section";
import { TabsProvider, useTabs } from "@/contexts/tabs-context";
import { SplitPanelProvider, useSplitPanel } from "@/contexts/split-panel-context";
import { SplitPanelContainer } from "./split-panel-container";
import {
	WorkspaceSidebarProvider,
	useWorkspaceSidebar,
} from "@/contexts/workspace-sidebar-context";
import { DndProvider } from "@/contexts/dnd-context";
import { TabContentRenderer } from "@/components/tab-content-renderer";
import { useRef, useEffect, useState } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";

import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from "@/components/ui/resizable";
import WorkspaceSidebar from "@/layout/components/workspace-sidebar";
import { Route as WorkspaceRoute } from "@/routes/$workspaceId/route";
import type { Workspace } from "@/utils/custom-types";
import { useWorkspaceContext, WorkspaceProvider } from "@/contexts/workspace-context";

const SIDEBAR_MIN_SIZE = 11;
const SIDEBAR_MAX_SIZE = 35;

const WorkspaceLayoutInner = () => {
	const [showToolbar, setShowToolbar] = useState<boolean>(true);
 
	const { session, currentWorkspace } =
		WorkspaceRoute.useLoaderData();

	const { channels, sidebarState } = useWorkspaceContext();

	const { pathname } = useLocation();
	const { isCollapsed, setIsCollapsed } = useWorkspaceSidebar();
	const { focusedTabId, tabs, panelsFocusMode, setFocusedTab, setPanelsFocusMode, setActiveTabForPanel } = useTabs();
	const sidebarPanelRef = useRef<ImperativePanelHandle>(null);
	const allowCollapseRef = useRef(false);

	// Handle collapse/expand
	useEffect(() => {
		if (sidebarPanelRef.current) {
			if (isCollapsed) {
				allowCollapseRef.current = true;
				sidebarPanelRef.current.collapse();
			} else {
				allowCollapseRef.current = false;
				sidebarPanelRef.current.expand(SIDEBAR_MIN_SIZE);
			}
		}
	}, [isCollapsed]);

	// If a tab is focused, render in full-screen mode with only that tab's content
	if (focusedTabId) {
		const focusedTab = tabs.find(t => t.id === focusedTabId);
		if (focusedTab) {
			return (
				<div className="flex h-screen flex-col overflow-hidden">
					<TabsSection
						panelId={focusedTab.panelId}
						panelActiveTabId={focusedTab.id}
						onTabClick={(tabId) => {
							setFocusedTab(tabId);
							setActiveTabForPanel(focusedTab.panelId as string, tabId);
						}}
						isInsideTabBar={true}
						preview={true}
					/>
					<TabContentRenderer tab={focusedTab} isFocused={true} />
				</div>
			);
		}
	}

	// If panels focus mode is active, hide sidebar and toolbar but show panels
	if (panelsFocusMode) {
		return (
			<div className="flex h-screen flex-col overflow-hidden">
				<Toolbar
					pathname={pathname}
					currentWorkspace={currentWorkspace}
					session={session}
					allChannels={channels}
				/>
				<SplitPanelContainer currentWorkspace={currentWorkspace} />
			</div>
		);
	}

	// Normal layout
	return (
		<div className="flex h-screen flex-col overflow-hidden">
			{/* <div
				className="absolute right-4 top-4 z-100"
				onClick={() => setShowToolbar(prev => !prev)}
			>
				<ArrowRight />
			</div> */}
			{showToolbar &&
				<Toolbar
					pathname={pathname}
					currentWorkspace={currentWorkspace}
					session={session}
					allChannels={channels}
				/>
			}
			<div className="flex flex-1 overflow-hidden bg-background">
				{/* <Sidebar currentWorkspaceId={currentWorkspace.id}/> */}
				<ResizablePanelGroup
					direction="horizontal"
					autoSaveId="workspace-layout"
					className="flex-1"
				>
					<ResizablePanel
						ref={sidebarPanelRef}
						defaultSize={20}
						minSize={SIDEBAR_MIN_SIZE}
						maxSize={SIDEBAR_MAX_SIZE}
						collapsible={true}
						collapsedSize={0}
						className="bg-sidebar"
						onCollapse={() => {
							if (allowCollapseRef.current) {
								allowCollapseRef.current = false;
								setIsCollapsed(true);
								return;
							}

							if (sidebarPanelRef.current) {
								sidebarPanelRef.current.resize(SIDEBAR_MIN_SIZE);
							}
						}}
						onExpand={() => {
							setIsCollapsed(false);
						}}
					>
						<WorkspaceSidebar
							currentWorkspace={currentWorkspace}
							// channels={channels}
						/>
					</ResizablePanel>
					<ResizableHandle />
					<ResizablePanel defaultSize={80} minSize={20}>
						<SplitPanelContainer currentWorkspace={currentWorkspace}/>
					</ResizablePanel>
				</ResizablePanelGroup>
			</div>
		</div>
	);
};

const WorkspaceLayoutContent = () => {
	return (
		<TabsProvider>
			<SplitPanelProvider>
				<DndProvider>
					<WorkspaceLayoutInner />
				</DndProvider>
			</SplitPanelProvider>
		</TabsProvider>
	);
};

export const WorkspaceLayout = () => {
	return (
		<WorkspaceProvider>
			<WorkspaceSidebarProvider>
				<WorkspaceLayoutContent />
			</WorkspaceSidebarProvider>
		</WorkspaceProvider>
	);
};
